import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { accountService } from "@/data/mock-accounts";
import { storage } from "@/lib/local-storage";
import { removeAuthToken } from "@/lib/api-client";

const STORAGE_KEY = "selectedAccountId";
const STORAGE_ACCOUNT_ID_KEY = "accountId";
const STORAGE_USER_TYPE_KEY = "userType";
const STORAGE_ACCOUNT_NAME_KEY = "accountName";
const DEFAULT_ACCOUNT_ID = "1"; // Shipper account

interface AccountContextType {
    selectedAccountId: string;
    isSwitching: boolean;
    switchAccount: (accountId: string) => void;
    signOut: () => void;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const useAccount = (): AccountContextType => {
    const context = useContext(AccountContext);
    if (context === undefined) {
        throw new Error("useAccount must be used within an AccountProvider");
    }
    return context;
};

interface AccountProviderProps {
    children: ReactNode;
}

export const AccountProvider = ({ children }: AccountProviderProps) => {
    const [selectedAccountId, setSelectedAccountId] = useState<string>(DEFAULT_ACCOUNT_ID);
    const [isSwitching, setIsSwitching] = useState(false);

    // Funzione helper per sincronizzare selectedAccountId da localStorage
    const syncAccountId = useCallback(() => {
        const storedAccountId = storage.getItem(STORAGE_ACCOUNT_ID_KEY);
        const storedSelectedId = storage.getItem(STORAGE_KEY);

        // Se c'è un accountId in localStorage, usalo (compatibilità con login)
        if (storedAccountId) {
            setSelectedAccountId(storedAccountId);
            storage.setItem(STORAGE_KEY, storedAccountId);
        } else if (storedSelectedId) {
            setSelectedAccountId(storedSelectedId);
        } else {
            // Imposta il default se non esiste
            storage.setItem(STORAGE_KEY, DEFAULT_ACCOUNT_ID);
            setSelectedAccountId(DEFAULT_ACCOUNT_ID);
        }
    }, []);

    // Inizializza da localStorage al mount
    useEffect(() => {
        syncAccountId();
    }, [syncAccountId]);

    // Listener per sincronizzare quando accountId cambia in localStorage
    useEffect(() => {
        // Handler per l'evento storage (cambiamenti da altre tab)
        const handleStorageChange = (e: StorageEvent) => {
            // Nota: StorageEvent contiene la chiave originale (non prefissata)
            // perché viene emesso quando localStorage cambia direttamente
            // Dobbiamo verificare se la chiave corrisponde alla chiave prefissata
            const prefix = import.meta.env.VITE_PREFIX || '';
            const prefixedKey = prefix ? `${prefix}_${STORAGE_ACCOUNT_ID_KEY}` : STORAGE_ACCOUNT_ID_KEY;
            if (e.key === prefixedKey && e.newValue) {
                setSelectedAccountId(e.newValue);
                storage.setItem(STORAGE_KEY, e.newValue);
            }
        };

        // Handler per custom event (cambiamenti nella stessa tab)
        const handleAccountIdChange = () => {
            syncAccountId();
        };

        // Handler per quando la pagina riceve focus (per sincronizzare dopo login)
        const handleFocus = () => {
            syncAccountId();
        };

        // Aggiungi listener per storage event (altre tab)
        window.addEventListener('storage', handleStorageChange);

        // Aggiungi listener per custom event (stessa tab)
        window.addEventListener('accountIdChanged', handleAccountIdChange);

        // Aggiungi listener per focus (per sincronizzare dopo login)
        window.addEventListener('focus', handleFocus);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('accountIdChanged', handleAccountIdChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [syncAccountId]);

    const switchAccount = useCallback((accountId: string) => {
        if (accountId === selectedAccountId) {
            return; // Già selezionato, non fare nulla
        }

        setIsSwitching(true);

        // Trova l'account e aggiorna localStorage
        const account = accountService.getById(accountId);
        if (account) {
            storage.setItem(STORAGE_KEY, accountId);
            storage.setItem(STORAGE_ACCOUNT_ID_KEY, account.id);
            storage.setItem(STORAGE_USER_TYPE_KEY, account.userType);
            storage.setItem(STORAGE_ACCOUNT_NAME_KEY, account.legalName);
        }

        // Simula 1.5 secondi di loading, poi redirect
        setTimeout(() => {
            setSelectedAccountId(accountId);
            setIsSwitching(false);

            // Redirect in base al tipo di account
            if (account) {
                if (account.userType === 'SHIPPER') {
                    window.location.href = '/shipper/dashboard';
                } else if (account.userType === 'RECEIVER') {
                    window.location.href = '/receiver/dashboard';
                } else if (account.userType === 'ADMIN') {
                    window.location.href = '/admin/dashboard';
                } else if (account.userType === 'FREIGHT_FORWARDER') {
                    window.location.href = '/freight-forwarder/dashboard';
                }
            }
        }, 1500);
    }, [selectedAccountId]);

    const signOut = useCallback(() => {
        // Rimuovi token di autenticazione (access_token, refresh_token)
        removeAuthToken();
        
        // Rimuovi tutte le informazioni utente dal localStorage
        storage.removeItems([
            STORAGE_KEY, 
            STORAGE_ACCOUNT_ID_KEY, 
            STORAGE_USER_TYPE_KEY, 
            STORAGE_ACCOUNT_NAME_KEY
        ]);

        // Reset allo stato di default
        setSelectedAccountId(DEFAULT_ACCOUNT_ID);

        // Redirect alla pagina di login
        window.location.href = '/auth/login';
    }, []);

    return (
        <AccountContext.Provider
            value={{
                selectedAccountId,
                isSwitching,
                switchAccount,
                signOut,
            }}
        >
            {children}
        </AccountContext.Provider>
    );
};

